import { useState, useEffect, useRef } from 'react'

import { ConfirmProvider } from "material-ui-confirm"
import { SnackbarProvider, closeSnackbar, enqueueSnackbar } from 'notistack'
import cookie from 'react-cookies'
import _ from "lodash"

import CssBaseline from '@mui/material/CssBaseline'
import { Dialog, DialogActions, DialogContent, DialogTitle, Button } from '@mui/material'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { grey, green, teal, blueGrey, lime, amber } from '@mui/material/colors'
import CloseIcon from '@mui/icons-material/Close'

import { checkLoggedIn, logout } from '/providers/auth-api'
import { getClientDetails, getCreditsBalance } from './providers/clients-api'
import { configureAuthenticatedApi } from '/providers/authenticated-api'
import { setClientScope } from './providers/client-scope'
import { getRealtimeConfig, getSupportThreads, saveUserPreferences } from '/providers/studio-api'
import { getClientTemplates, getClientTemplate, getClientImages, getSystemTemplates } from '/providers/templates-api'
import { getPass } from '/providers/passes-api'
import { startStudioRealtime } from '/utils/studio-realtime'

import { AppContext } from "/providers/app-context"
import AppRoutes from './AppRoutes'
import smartixLogo from '/assets/images/smartix-logo.png'

const selectedClientStorageKey = 'smartix.selectedClientId'

const normaliseClientId = clientId => {
  if (clientId == null || clientId === '') return null
  return String(clientId)
}

const getStoredSelectedClientId = () => {
  try {
    const value = window.localStorage.getItem(selectedClientStorageKey)
    return normaliseClientId(value)
  }
  catch {
    return null
  }
}

const setStoredSelectedClientId = clientId => {
  try {
    if (clientId == null) {
      window.localStorage.removeItem(selectedClientStorageKey)
    } else {
      window.localStorage.setItem(selectedClientStorageKey, normaliseClientId(clientId))
    }
  }
  catch {
    return null
  }
}

const getInitialSelectedClientId = (authenticatedClientId, client) => {
  const storedClientId = getStoredSelectedClientId()
  const defaultClientId = normaliseClientId(authenticatedClientId)
  if (storedClientId == null) return defaultClientId

  const availableClientIds = [
    defaultClientId,
    ...(client?.available_clients || []).map(item => normaliseClientId(item.client_id))
  ]

  return availableClientIds.includes(storedClientId) ? storedClientId : defaultClientId
}

const mergeTemplateSyncState = (template, syncState) => {
  if (!template || !syncState) return template
  const nextUniqueSyncCount = Number(syncState.unique_sync_count ?? template?.unique_sync_count ?? 0)

  return {
    ...template,
    apple_sync_revision: syncState.apple_sync_revision,
    google_sync_revision: syncState.google_sync_revision,
    apple_checked_revision: syncState.apple_checked_revision,
    google_checked_revision: syncState.google_checked_revision,
    apple_check_status: syncState.apple_check_status,
    google_check_status: syncState.google_check_status,
    apple_candidate_total: syncState.apple_candidate_total,
    google_candidate_total: syncState.google_candidate_total,
    apple_checked_so_far: syncState.apple_checked_so_far,
    google_checked_so_far: syncState.google_checked_so_far,
    apple_check_cursor_pass_id: syncState.apple_check_cursor_pass_id,
    google_check_cursor_pass_id: syncState.google_check_cursor_pass_id,
    apple_sync_count: syncState.apple_sync_count,
    google_sync_count: syncState.google_sync_count,
    unique_sync_count: syncState.unique_sync_count,
    unique_live_sync_count: syncState.unique_live_sync_count ?? template.unique_live_sync_count ?? 0,
    unique_test_sync_count: syncState.unique_test_sync_count ?? template.unique_test_sync_count ?? 0,
    apple_check_requested_date: syncState.apple_check_requested_date,
    google_check_requested_date: syncState.google_check_requested_date,
    apple_check_started_date: syncState.apple_check_started_date,
    google_check_started_date: syncState.google_check_started_date,
    apple_check_completed_date: syncState.apple_check_completed_date,
    google_check_completed_date: syncState.google_check_completed_date,
    apple_error_report: syncState.apple_error_report,
    google_error_report: syncState.google_error_report,
    sync_progress: syncState.sync_progress ?? template.sync_progress ?? null,
    sync_execution_in_progress: Boolean(template?.sync_execution_in_progress) && nextUniqueSyncCount > 0,
  }
}

const theme = createTheme({
  typography: {
    // Force all typography variants to use px
    pxToRem: (px) => `${Math.round(px)}px`
  },
  colorSchemes: {
    light: {
      palette: {
        type: "light",
        primary: {
          main: teal[700],
          light: teal[200],
        },
        menubar: {
          bg: teal[100],
          fg: "#303030"
        },
        table: {
          row: blueGrey[700],
          // Keep table headers/footers and the help/settings drawers close to
          // the light navigation surface, with just enough contrast to read
          // as a distinct surface.
          head: '#9fc9c5',
        },
        paper: grey[200],
        secondary: {
          main: green[300]
        },
        lightgray: blueGrey[400],
        darkgray: blueGrey[600],
      },
    },
    dark: {
      palette: {
        type: "dark",
        primary: {
          main: amber[200],
          light: amber[100]
        },
        secondary: {
          main: lime[300]
        },
        table: {
          row: '#1E1E1E',
          head: '#404040',
        },
        paper: "#fff",
        menubar: {
          bg: '#1E1E1E',
          fg: '#f4f4f6'
        },
        lightgray: blueGrey[300],
        darkgray: blueGrey[600],
      },
    },
  },
  components: {
      MuiCssBaseline: {
        styleOverrides: theme => ({
          body: {
            backgroundColor: theme.palette.mode === 'dark' ? '#303030' : '#f4f4f6',
            backgroundSize: '16px 16px',
          },
          '.dashboard h1, .dashboard h2, .dashboard h3, .dashboard h4, .dashboard h5, .dashboard h6': {
            color: theme.palette.primary.main,
          },
        }),
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            // Outlined TextField 
            "& .MuiOutlinedInput-root": {
              fontFamily: "Arial",
              fontWeight: "bold",
              "& .MuiOutlinedInput-notchedOutline": {
                borderWidth: "1px",
              },
              "&.Mui-focused": {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderWidth: "3px",
                },
              },
              "&.Mui-disabled": {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderWidth: "1px",
                },
              },
              "&:hover:not(.Mui-focused,.Mui-disabled)": {
                "& .MuiOutlinedInput-notchedOutline": {
                },
              },
            },
            "& .MuiInputLabel-outlined": {
              fontWeight: "bold",
              "&.Mui-focused": {
                color: "secondary.main",
              },
            },
          },
        },
      },
    },
  })


const checkLoginToken = async ({ apiSource, setLoading, applyLoginResponse }) => {
  try {
    let loginResponse = await checkLoggedIn({ apiSource })

    if (loginResponse.authenticated) {
      await applyLoginResponse(loginResponse, { preserveSelectedClient: true })
    }

    setLoading(false)
  }
  catch (err) {
    setLoading(false)
    return null
  }
}

const checkApiSourceCookie = () => {
  if (!["localhost", "127.0.0.1", "local.smartix.uk"].includes(window.location.hostname)) return "remote" // Always remote, regardless of cookie when using deployed app

  let s = cookie.load("apiSource")
  if (!s) {
    s = "remote"
    setApiSourceCookie(s)
  }

  return s
}

const setApiSourceCookie = source => {
  cookie.save('apiSource', source, { path: '/' })
}

const App = () => {
  const [loading, setLoading] = useState(true)
  const [isViewportTooSmall, setIsViewportTooSmall] = useState(
    typeof window !== 'undefined' && (window.innerWidth < 900 || window.innerHeight < 900)
  )

  const [apiSource, setApiSource] = useState(checkApiSourceCookie())
  const [apiKey, setApiKey] = useState(null)
  const [user, setUser] = useState(null)
  const [authenticatedClient, setAuthenticatedClient] = useState(null)
  const [client, setClient] = useState(null)
  const [selectedClientId, setSelectedClientId] = useState(null)
  const [creditBalance, setCreditBalance] = useState(null)
  const [realtimeConfig, setRealtimeConfig] = useState(null)
  const [realtimeConfigWarningShown, setRealtimeConfigWarningShown] = useState(false)
  const [templates, setTemplates] = useState([])
  const [systemTemplates, setSystemTemplates] = useState([])
  const [passes, setPasses] = useState([])
  const [processing, setProcessing] = useState(false)
  // A deep link can render before the initial template request starts. Keep
  // guards in template sub-pages in a loading state until that request has
  // completed, otherwise a browser refresh redirects to /templates.
  const [loadingTemplates, setLoadingTemplates] = useState(true)
  const [showHelp, setShowHelp] = useState(false)
  const [imageList, setImageList] = useState([])
  const [speeddialVisible, setSpeeddialVisible] = useState(false)
  const [copyingEmail, setCopyingEmail] = useState(false)
  const [emailBarcodes, setEmailBarcodes] = useState(false)
  const [giftCard, setGiftCard] = useState(null)
  const [supportUnreadCount, setSupportUnreadCount] = useState(0)
  const [authFailureDialogOpen, setAuthFailureDialogOpen] = useState(false)
  const [viewportWarningDismissed, setViewportWarningDismissed] = useState(false)
  const templateRefreshInProgress = useRef(null)

  const clearAuthState = () => {
    setClientScope({
      authenticatedClient: null,
      selectedClient: null
    })
    setStoredSelectedClientId(null)
    setApiKey(null)
    setUser(null)
    setAuthenticatedClient(null)
    setSelectedClientId(null)
    setClient(null)
    setCreditBalance(null)
    setRealtimeConfig(null)
    setTemplates([])
    setSystemTemplates([])
    setPasses([])
    setImageList([])
    setSupportUnreadCount(0)
  }

  const logoutUser = async () => {
    try {
      await logout({ apiSource })
    }
    catch {
      // Ignore logout failures during forced sign-out
    }

    clearAuthState()
    setAuthFailureDialogOpen(false)
    window.history.replaceState({}, '', '/login')
  }

  const applyLoginResponse = async (loginResponse, { preserveSelectedClient = false } = {}) => {
    setApiKey(loginResponse.key)
    setAuthenticatedClient(loginResponse.client)
    setUser(loginResponse.user)

    const clientResponse = await getClientDetails({ apiSource, apiKey: loginResponse.key })
    const nextClient = clientResponse.ok ? clientResponse.client : loginResponse.client
    const nextSelectedClientId = preserveSelectedClient
      ? getInitialSelectedClientId(loginResponse.client.client_id, nextClient)
      : normaliseClientId(loginResponse.client.client_id)

    setClient(nextClient)
    setSelectedClientId(nextSelectedClientId)
    setClientScope({
      authenticatedClient: loginResponse.client.client_id,
      selectedClient: nextSelectedClientId
    })
  }

  const handleAuthenticationFailure = async () => {
    setAuthFailureDialogOpen(true)
  }

  const saveUser = async u => {
    setUser(u)
    await saveUserPreferences({ apiSource, apiKey, user: u })
  }

  const refreshSupportUnreadCount = async () => {
    if (!apiKey) {
      setSupportUnreadCount(0)
      return
    }

    const result = await getSupportThreads({ apiSource, apiKey })
    if (result.ok) setSupportUnreadCount(Number(result.unread_count ?? 0))
  }

  const refreshTemplates = async ({ silent = false, force = false } = {}) => {
    if (templateRefreshInProgress.current) {
      const inProgressRefresh = templateRefreshInProgress.current
      await inProgressRefresh
      if (!force) return
    }

    if (!silent) setLoadingTemplates(true)

    const refresh = (async () => {
      try {
        const tt = await getClientTemplates({ apiSource, apiKey })
        if (!tt.error) setTemplates(tt)
      }
      finally {
        if (!silent) setLoadingTemplates(false)
      }
    })()

    templateRefreshInProgress.current = refresh
    try {
      await refresh
    }
    finally {
      if (templateRefreshInProgress.current === refresh) templateRefreshInProgress.current = null
    }
  }

  const refreshFileList = async () => {
    const result = await getClientImages({ apiSource, apiKey })
    //todo handler error
    if (result.ok) {
      setImageList(result.images)
    } else {
      enqueueSnackbar({ message: result.error, variant: "error" })
    }
  }

  const refreshTemplate = async templateId => {
    setLoadingTemplates(true)
    const reloadedTemplate = await getClientTemplate({ apiSource, apiKey, templateId })

    if (!reloadedTemplate.error) {
      const tt = _.cloneDeep(templates)
      const index = _.findIndex(tt, i => i.template_id === templateId)

      if (index !== -1) {
        tt[index] = reloadedTemplate
        setTemplates(tt)
      }
    }
    // TODO handle error
    setLoadingTemplates(false)
  }

  const refreshClient = async () => {
    const c = await getClientDetails({ apiSource, apiKey })
    if (c.ok) setClient(c.client)
    // TODO Add error handling
  }

  const refreshCreditBalance = async () => {
    if (!apiKey || !client || client.billing_mode !== "prepay" || selectedClientId == null) {
      setCreditBalance(null)
      return
    }

    const balanceResult = await getCreditsBalance({ apiSource, apiKey })
    if (balanceResult.ok) {
      setCreditBalance(balanceResult.balance ?? null)
    } else {
      setCreditBalance(null)
    }
  }

  const refreshRealtimeConfig = async () => {
    if (!apiKey || !authenticatedClient) {
      setRealtimeConfig(null)
      setRealtimeConfigWarningShown(false)
      return
    }

    const result = await getRealtimeConfig({ apiSource, apiKey })
    const realtimeUrls = result.ok
      ? (
        Array.isArray(result.websocket_urls)
          ? result.websocket_urls.filter(Boolean)
          : (result.websocket_url ? [result.websocket_url] : [])
      )
      : []

    if (realtimeUrls.length) {
      setRealtimeConfig(realtimeUrls)
      setRealtimeConfigWarningShown(false)
    } else {
      setRealtimeConfig(null)
      if (!realtimeConfigWarningShown) {
        enqueueSnackbar({
          message: 'Realtime updates are unavailable (invalid websocket config)',
          variant: 'warning'
        })
        setRealtimeConfigWarningShown(true)
      }
    }
  }

  const selectClient = clientId => {
    const nextClientId = normaliseClientId(clientId)
    setClientScope({
      authenticatedClient: authenticatedClient?.client_id,
      selectedClient: nextClientId
    })
    setSelectedClientId(nextClientId)
  }

  const refreshPass = async ({ templateId, passId }) => {
    const response = await getPass({ apiSource, apiKey, passId })
    const reloadedPass = response?.pass
    if (!reloadedPass) return

    setPasses(current => {
      const index = current.findIndex(i => i.template_id === templateId && i.pass_id === passId)
      if (index === -1) return current

      const next = [...current]
      next[index] = reloadedPass
      return next
    })
  }

  useEffect(() => {
    checkLoginToken({ apiSource, setLoading, applyLoginResponse })
  }, [])

  useEffect(() => {
    refreshSupportUnreadCount()
  }, [apiKey, apiSource])

  useEffect(() => {
    configureAuthenticatedApi({
      applyLoginResponse,
      handleAuthenticationFailure,
    })
  }, [apiSource, authenticatedClient, selectedClientId])

  useEffect(() => {
    setClientScope({
      authenticatedClient: authenticatedClient?.client_id,
      selectedClient: selectedClientId
    })
  }, [authenticatedClient, selectedClientId])

  useEffect(() => {
    if (loading) return

    if (!authenticatedClient) {
      setStoredSelectedClientId(null)
      return
    }

    if (selectedClientId != null) {
      setStoredSelectedClientId(selectedClientId)
    }
  }, [authenticatedClient, selectedClientId, loading])

  useEffect(() => {
    if (!loading) setApiSourceCookie(apiSource)
  }, [apiSource])

  useEffect(() => {
    if (!loading && apiKey && selectedClientId != null) {
      setLoadingTemplates(true)
      refreshTemplates()
      refreshFileList()
    }
  }, [apiKey, apiSource, loading, selectedClientId])

  useEffect(() => {
    if (loading || !apiKey) {
      setSystemTemplates([])
      return
    }

    let cancelled = false
    getSystemTemplates({ apiSource, apiKey }).then(result => {
      if (!cancelled && !result?.error) setSystemTemplates(result)
    })

    return () => { cancelled = true }
  }, [apiKey, apiSource, loading])

  useEffect(() => {
    if (!loading && apiKey && selectedClientId != null) {
      refreshCreditBalance()
    } else if (!apiKey || selectedClientId == null) {
      setCreditBalance(null)
    }
  }, [apiKey, apiSource, client?.billing_mode, loading, selectedClientId])

  useEffect(() => {
    if (!loading && apiKey && authenticatedClient) {
      refreshRealtimeConfig()
    } else {
      setRealtimeConfig(null)
    }
  }, [apiKey, apiSource, authenticatedClient, loading])

  useEffect(() => {
    if (!apiKey || !realtimeConfig || !authenticatedClient) return

    return startStudioRealtime({
      baseUrl: realtimeConfig,
      token: apiKey,
      onConnected: ({ reconnected }) => {
        if (reconnected) refreshTemplates({ silent: true })
      },
      onEvent: (event) => {
        const eventClientId = normaliseClientId(event?.payload?.client_id ?? event?.client_id)
        const acceptedClientIds = new Set(
          [
            authenticatedClient?.client_id,
            authenticatedClient?.parent_client_id,
            client?.client_id,
            client?.parent_client_id,
            selectedClientId
          ]
            .map(normaliseClientId)
            .filter(id => id != null)
        )

        // Realtime stream should already be scoped server-side, but keep a soft guard.
        if (eventClientId != null && acceptedClientIds.size && !acceptedClientIds.has(eventClientId)) {
          console.log('[studio-realtime] ignored event for unmatched client', {
            eventType: event?.type,
            eventClientId,
            acceptedClientIds: [...acceptedClientIds]
          })
          return
        }

        if (event?.type === 'credit_balance_updated') {
          setCreditBalance(event?.payload ?? null)
          return
        }

        if (event?.type === 'template_sync_state_updated') {
          const templateId = event?.payload?.template_id
          if (!templateId) return

          setTemplates(currentTemplates => {
            const nextTemplates = _.cloneDeep(currentTemplates)
            const index = _.findIndex(nextTemplates, template => template.template_id === templateId)
            if (index === -1) return currentTemplates

            nextTemplates[index] = mergeTemplateSyncState(nextTemplates[index], event.payload)
            return nextTemplates
          })
        }
      }
    })
  }, [apiKey, authenticatedClient, realtimeConfig])

  useEffect(() => {
    const handleViewportChange = () => {
      setIsViewportTooSmall(window.innerWidth < 900 || window.innerHeight < 900)
    }

    handleViewportChange()
    window.addEventListener('resize', handleViewportChange)
    window.addEventListener('orientationchange', handleViewportChange)

    return () => {
      window.removeEventListener('resize', handleViewportChange)
      window.removeEventListener('orientationchange', handleViewportChange)
    }
  }, [])

  return (
    <ThemeProvider theme={theme} noSsr>
        <CssBaseline enableColorScheme />
        <AppContext.Provider value={{ apiSource, setApiSource, apiKey, setApiKey, copyingEmail, setCopyingEmail, emailBarcodes, setEmailBarcodes, giftCard, setGiftCard, loadingTemplates, processing, setProcessing, speeddialVisible, setSpeeddialVisible, showHelp, setShowHelp, user, setUser, saveUser, authenticatedClient, setAuthenticatedClient, selectedClientId, setSelectedClientId, selectClient, client, setClient, refreshClient, creditBalance, refreshCreditBalance, templates, setTemplates, systemTemplates, refreshTemplate, refreshTemplates, passes, setPasses, refreshPass, imageList, setImageList, supportUnreadCount, refreshSupportUnreadCount, applyLoginResponse, logoutUser }}>
          <ConfirmProvider defaultOptions={{ confirmationButtonProps: { autoFocus: true } }}>
            <SnackbarProvider
              autoHideDuration={3000}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              action={(snackbarId) => (
                <button onClick={() => closeSnackbar(snackbarId)}>
                  <CloseIcon />
                </button>
              )}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <AppRoutes client={client} loading={loading} />
              </LocalizationProvider>
              <Dialog open={authFailureDialogOpen} disableEscapeKeyDown>
                <DialogTitle>Session expired</DialogTitle>
                <DialogContent>
                  Your login is no longer valid. Please sign in again.
                </DialogContent>
                <DialogActions>
                  <Button variant='contained' onClick={logoutUser}>Sign out</Button>
                </DialogActions>
              </Dialog>

              <Dialog
                open={isViewportTooSmall && !viewportWarningDismissed}
                onClose={() => setViewportWarningDismissed(true)}
                fullWidth
                maxWidth='sm'
              >
                <DialogTitle>Smartix Studio</DialogTitle>
                <DialogContent>
                  <div style={{ width: '100%', textAlign: 'center' }}>
                    <img
                      src={smartixLogo}
                      alt="Smartix"
                      style={{ width: '220px', maxWidth: '70%', height: 'auto', margin: '0 auto 14px auto' }}
                    />
                    <p style={{ margin: 0, lineHeight: 1.6, fontSize: '1rem' }}>
                      Studio is a complex App that is impractical to use on a mobile device or small screen.
                      A browser size of 900px x 900px is the minimum requirement. Ideally 1300px x 900px.
                    </p>
                  </div>
                </DialogContent>
                <DialogActions>
                  <Button variant='contained' onClick={() => setViewportWarningDismissed(true)}>Continue</Button>
                </DialogActions>
              </Dialog>
            </SnackbarProvider>
          </ConfirmProvider>
        </AppContext.Provider>
      </ThemeProvider>
  )
}

export default App
